import { Injectable, signal } from '@angular/core';
import posthog from 'posthog-js';
import { environment } from '../../../environments/environment';

@Injectable({
  providedIn: 'root',
})
export class FeatureFlagService {
  /** Muda quando as flags terminam de carregar — usado para reatividade no nav. */
  loaded = signal(false);

  /**
   * Incrementa a CADA atualização de flags (o PostHog carrega 1ª vez como
   * anônimo e recarrega após o identify do login). Consumidores reativos
   * (ex.: menu) devem ler este signal para recomputar quando o valor real
   * da flag chegar — sem exigir reload da página.
   */
  flagsVersion = signal(0);

  private initialized = false;
  private readyResolve!: () => void;
  /** Resolve quando as flags estão disponíveis (ou quando não há PostHog configurado). */
  ready = new Promise<void>((resolve) => (this.readyResolve = resolve));

  init(): void {
    if (this.initialized) return;
    this.initialized = true;

    const config = environment.posthog;
    if (!config?.key) {
      // Sem PostHog configurado: segue o fluxo; isEnabled usa o fallback.
      this.markReady();
      return;
    }

    posthog.init(config.key, {
      api_host: config.host,
      capture_pageview: false,
      autocapture: false,
      person_profiles: 'identified_only',
      // Session Replay (gravação de sessão) — apenas em ambientes deployados
      // (sessionRecording=true em dev/prod, false no local). Também precisa
      // estar ativo em Project Settings -> Session Replay no PostHog. Inputs
      // são mascarados por privacidade.
      disable_session_recording: !config.sessionRecording,
      session_recording: {
        maskAllInputs: true,
        // Captura headers e CORPO das requisições fetch/XHR no Session Replay
        // (payloads de cálculo viram evidência reprodutível). Limite do
        // PostHog: ~1MB por corpo — acima disso ele descarta o body. Também
        // exige ligar "Capture headers/body" em Settings > Session Replay
        // no painel do PostHog.
        recordHeaders: true,
        recordBody: true,
        // NUNCA gravar credenciais: as chamadas do Firebase Auth carregam a
        // SENHA no corpo (signInWithPassword) e tokens na resposta
        // (securetoken). O header Authorization o PostHog já remove sozinho.
        maskCapturedNetworkRequestFn: (request) => {
          if (/identitytoolkit|securetoken/i.test(request.name || '')) {
            request.requestBody = '[redigido: credenciais]';
            request.responseBody = '[redigido: credenciais]';
          }
          return request;
        },
      },
    });

    posthog.onFeatureFlags(() => {
      this.flagsVersion.update((v) => v + 1);
      this.markReady();
    });

    // Fallback: não travar a navegação caso o PostHog não responda.
    setTimeout(() => this.markReady(), 4000);
  }

  /**
   * Retorna se a flag está ligada. Sem PostHog configurado, usa o fallback
   * (padrão: visível). Com PostHog, respeita o valor da flag — oculto se a
   * flag estiver desligada ou não existir.
   */
  isEnabled(flag: string, fallback = true): boolean {
    if (!environment.posthog?.key) return fallback;
    // Resolve a chave lógica (ex.: 'reports') para o nome real da flag no
    // ambiente atual (ex.: 'relatorios-dev' / 'relatorios-prod').
    const flagKey = environment.featureFlags?.[flag] ?? flag;
    const value = posthog.isFeatureEnabled(flagKey);
    return value === undefined ? fallback : value;
  }

  identify(id: string, properties?: Record<string, unknown>): void {
    if (!environment.posthog?.key) return;
    posthog.identify(id, properties);
  }

  reset(): void {
    if (!environment.posthog?.key) return;
    posthog.reset();
  }

  private markReady(): void {
    this.loaded.set(true);
    this.readyResolve();
  }
}
